import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  Pencil, Trash, X, Check, Sparkle,
  User, UserPlus, EnvelopeSimple, Buildings, IdentificationCard,
  ToggleLeft, ToggleRight, MagnifyingGlass, CaretDown, Warning, ArrowsClockwise
} from "@phosphor-icons/react";

const DEPARTMENTS = [
  "Accounts Payable", "Accounts Receivable", "Procurement","Inventory Management","Manufacturing","Maintenance",
  "General Ledger", "Human Resources", "Treasury", "Finance", "Other",
];

const ROLES = ["user", "maker", "checker", "viewer"];

const EMPTY_FORM = {
  name: "", email: "", password: "",
  department: "", role: "user",
};

// ── Small status pill ──────────────────────────────────────────────────────
function StatusPill({ status }) {
  const active = status === "active";
  return (
    <span className={`inline-block text-[9px] tracking-[0.2em] uppercase font-bold px-2 py-0.5 border ${
      active 
        ? "border-emerald-500/30 text-emerald-400 bg-emerald-500/10 dark:border-emerald-500/40 dark:text-emerald-400" 
        : "border-gray-300 text-gray-500 bg-gray-50 dark:border-slate-700 dark:text-slate-400 dark:bg-slate-800"
    }`}>
      {status}
    </span>
  );
}

// ── Inline edit row ────────────────────────────────────────────────────────
function EditRow({ user, onSave, onCancel }) {
  const [f, setF] = useState({ name: user.name || "", department: user.department || "", role: user.role || "user" });
  const save = async () => {
    await api.put(`/users/${user.id}`, f);
    onSave();
  };
  return (
    <tr className="bg-blue-50/50 dark:bg-slate-800/80 border-b border-blue-100 dark:border-slate-700">
      <td className="px-4 py-3 font-mono text-xs text-slate-500 dark:text-slate-400">{user.email}</td>
      <td className="px-4 py-3">
        <input 
          value={f.name} 
          onChange={(e) => setF({ ...f, name: e.target.value })}
          className="w-full border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-600" 
        />
      </td>
      <td className="px-4 py-3">
        <select 
          value={f.role} 
          onChange={(e) => setF({ ...f, role: e.target.value })}
          className="border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-2 py-1 text-xs w-full uppercase"
        >
          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      </td>
      <td className="px-4 py-3">
        <select 
          value={f.department} 
          onChange={(e) => setF({ ...f, department: e.target.value })}
          className="border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-2 py-1 text-xs w-full"
        >
          <option value="">— None —</option>
          {DEPARTMENTS.map((d) => <option key={d}>{d}</option>)}
        </select>
      </td>
      <td className="px-4 py-3 text-right" colSpan={2}>
        <button onClick={save} className="text-blue-600 dark:text-blue-400 hover:underline text-xs font-bold mr-3 inline-flex items-center gap-1">
          <Check size={12} weight="bold" /> Save
        </button>
        <button onClick={onCancel} className="text-gray-500 dark:text-slate-400 hover:underline text-xs font-bold inline-flex items-center gap-1">
          <X size={12} weight="bold" /> Cancel
        </button>
      </td>
    </tr>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

export default function UsersPage() {
  const [users, setUsers]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId]   = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [form, setForm]       = useState(EMPTY_FORM);
  const [formErr, setFormErr] = useState("");
  const [formBusy, setFormBusy] = useState(false);
  const [search, setSearch]   = useState("");

  // Delete modal state
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteConfirmationText, setDeleteConfirmationText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  // Status toggle modal state
  const [userToToggleStatus, setUserToToggleStatus] = useState(null);
  const [isTogglingStatus, setIsTogglingStatus] = useState(false);

  const visibleUsers = users
    .filter((u) => !u.is_admin)
    .filter((u) => {
      const q = search.trim().toLowerCase();
      if (!q) return true;
      return u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
    });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const createUser = async (e) => {
    e.preventDefault();
    setFormErr(""); setFormBusy(true);
    try {
      await api.post("/users", form);
      setDrawerOpen(false);
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      setFormErr(typeof d === "string" ? d : "Failed to create user.");
    } finally {
      setFormBusy(false);
    }
  };

  // Trigger status popup modal
  const handleInitiateToggleStatus = (u) => {
    setUserToToggleStatus(u);
  };

  // Perform backend status update
  const confirmToggleStatus = async (e) => {
    e.preventDefault();
    if (!userToToggleStatus) return;

    const newStatus = userToToggleStatus.status === "active" ? "inactive" : "active";
    setIsTogglingStatus(true);
    try {
      await api.put(`/users/${userToToggleStatus.id}`, { status: newStatus });
      setUserToToggleStatus(null);
      load();
    } catch (err) {
      console.error("Failed to update user status.", err);
    } finally {
      setIsTogglingStatus(false);
    }
  };

  // Trigger delete popup modal
  const handleInitiateDelete = (u) => {
    setUserToDelete(u);
    setDeleteConfirmationText("");
  };

  // Perform backend deletion
  const confirmDeleteUser = async (e) => {
    e.preventDefault();
    if (!userToDelete || deleteConfirmationText !== userToDelete.email) return;

    setIsDeleting(true);
    try {
      await api.delete(`/users/${userToDelete.id}`);
      setUserToDelete(null);
      setDeleteConfirmationText("");
      load();
    } catch (err) {
      console.error("Failed to delete user.", err);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="bg-[#F7F8FA] dark:bg-[#0B1120] min-h-screen font-sans text-slate-800 dark:text-slate-200">
      {/* Header Container */}
      <div className="bg-white dark:bg-[#0B1120] border-b border-gray-200 dark:border-slate-800/80">
        <PageHeader
          eyebrow="Admin Console"
          title="Users & Access"
          subtitle="Every user across all tenants. Manage role, main-menu visibility, and per-agent overrides from one place."
          testid="users-title"
        />
      </div>

      {/* Search Bar + Controls */}
      <div className="px-8 pt-8 flex items-center justify-between gap-4">
        <div className="relative w-full max-w-md">
          <MagnifyingGlass size={14} weight="bold" className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by email, name, company, role..."
            data-testid="users-search"
            className="w-full bg-white dark:bg-[#131C31] border border-gray-200 dark:border-slate-700/60 rounded-none pl-10 pr-3 py-2.5 text-xs text-slate-900 dark:text-slate-200 placeholder:text-gray-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-600 dark:focus:ring-blue-500"
          />
        </div>

        <div className="flex items-center gap-6 shrink-0">
          <span className="text-xs text-slate-500 dark:text-slate-400" data-testid="users-count">
            {visibleUsers.length} of {users.filter((u) => !u.is_admin).length} users
          </span>
          <button 
            onClick={() => setDrawerOpen(true)}
            data-testid="invite-user-btn" 
            className="inline-flex items-center gap-1.5 px-4 py-2.5 text-[11px] font-bold uppercase tracking-wider bg-[#0020C2] hover:bg-[#0018a3] text-white transition-colors"
          >
            <UserPlus size={16} weight="bold" /> INVITE USER
          </button>
        </div>
      </div>

      {/* Table Section */}
      <div className="p-8">
        <div className="border border-gray-200 dark:border-slate-800/80 rounded-none bg-white dark:bg-[#0F172A] overflow-hidden">
          <table className="w-full text-sm" data-testid="users-table">
            <thead className="bg-white dark:bg-[#0F172A] border-b border-gray-200 dark:border-slate-800">
              <tr className="text-left">
                {["EMAIL", "NAME", "ROLE", "DEPARTMENT", "STATUS", "ACTIONS"].map((h) => (
                  <th key={h} className="px-5 py-4 text-[11px] tracking-[0.15em] uppercase font-bold text-gray-400 dark:text-slate-400">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-slate-800/60">
              {loading && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-sm text-gray-500 dark:text-slate-400">Loading…</td>
                </tr>
              )}
              {!loading && visibleUsers.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-sm text-gray-500 dark:text-slate-400">
                    {search ? "No users match your search." : <>No users yet. Click <strong>Invite User</strong> to create one.</>}
                  </td>
                </tr>
              )}
              {!loading && visibleUsers.map((u) =>
                editId === u.id ? (
                  <EditRow key={u.id} user={u} onSave={() => { setEditId(null); load(); }} onCancel={() => setEditId(null)} />
                ) : (
                  <tr key={u.id} className="hover:bg-gray-50 dark:hover:bg-slate-800/40 transition-colors" data-testid={`user-row-${u.id}`}>
                    <td className="px-5 py-4 font-mono text-xs text-gray-600 dark:text-slate-300 font-normal">
                      {u.email}
                    </td>

                    <td className="px-5 py-4 font-medium text-gray-900 dark:text-white">
                      {u.name}
                    </td>

                    <td className="px-5 py-4">
                      <div className="relative inline-block">
                        <select
                          value={u.role || "user"}
                          onChange={async (e) => {
                            await api.put(`/users/${u.id}`, { role: e.target.value });
                            load();
                          }}
                          className="appearance-none bg-gray-100 dark:bg-[#17223B] text-slate-800 dark:text-slate-200 border border-transparent dark:border-slate-700/50 text-[11px] font-mono font-bold uppercase tracking-wider px-3 py-1.5 pr-7 rounded-none focus:outline-none focus:ring-1 focus:ring-blue-500"
                        >
                          {ROLES.map((r) => (
                            <option key={r} value={r} className="bg-white dark:bg-[#0F172A] text-slate-900 dark:text-slate-200">
                              {r.toUpperCase()}
                            </option>
                          ))}
                        </select>
                        <CaretDown size={10} weight="bold" className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400" />
                      </div>
                    </td>

                    <td className="px-5 py-4 text-xs text-gray-600 dark:text-slate-300">
                      {u.department || <span className="text-gray-300 dark:text-slate-600">—</span>}
                    </td>

                    <td className="px-5 py-4">
                      <StatusPill status={u.status || "active"} />
                    </td>

                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3 justify-end">
                        <Link
                          to={`/admin/user-agents/${u.id}`}
                          data-testid={`assign-agents-${u.id}`}
                          className="text-[#0014D8] dark:text-blue-400 hover:underline text-xs font-bold inline-flex items-center gap-1"
                          title="Assign agents"
                        >
                          <Sparkle size={12} weight="fill" /> Agents
                        </Link>

                        <button 
                          onClick={() => setEditId(u.id)} 
                          data-testid={`edit-user-${u.id}`}
                          className="text-gray-700 dark:text-slate-300 hover:underline text-xs font-bold inline-flex items-center gap-1"
                        >
                          <Pencil size={12} weight="bold" /> Edit
                        </button>

                        <button 
                          onClick={() => handleInitiateToggleStatus(u)} 
                          data-testid={`toggle-status-${u.id}`}
                          className="text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white text-xs font-bold inline-flex items-center gap-1"
                          title={u.status === "active" ? "Deactivate" : "Reactivate"}
                        >
                          {u.status === "active"
                            ? <ToggleRight size={18} weight="bold" className="text-emerald-500" />
                            : <ToggleLeft size={18} weight="bold" className="text-slate-500" />}
                        </button>

                        <button 
                          onClick={() => handleInitiateDelete(u)} 
                          data-testid={`delete-user-${u.id}`}
                          className="text-rose-600 dark:text-rose-400 hover:underline text-xs font-bold inline-flex items-center gap-1"
                        >
                          <Trash size={12} weight="bold" />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Status Toggle Confirmation Modal ── */}
      {userToToggleStatus && (
        <div className="fixed inset-0 bg-black/10 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <form
            onSubmit={confirmToggleStatus}
            className="w-full max-w-md bg-white dark:bg-[#0F172A] border dark:border-slate-800 flex flex-col shadow-2xl"
            data-testid="toggle-status-modal"
          >
            <div className="p-6 border-b border-gray-200 dark:border-slate-800 flex items-start justify-between">
              <div className="flex items-center gap-2 text-amber-500">
                <ArrowsClockwise size={20} weight="bold" />
                <h2 className="text-lg font-bold tracking-tight text-slate-900 dark:text-white">
                  {userToToggleStatus.status === "active" ? "Deactivate User" : "Reactivate User"}
                </h2>
              </div>
              <button 
                type="button" 
                onClick={() => setUserToToggleStatus(null)} 
                className="shrink-0 text-slate-400"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-6">
              <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                Are you sure you want to mark <strong className="text-slate-900 dark:text-white font-semibold">{userToToggleStatus.name}</strong> ({userToToggleStatus.email}) as{" "}
                <span className={`font-bold ${userToToggleStatus.status === "active" ? "text-rose-400" : "text-emerald-400"}`}>
                  {userToToggleStatus.status === "active" ? "INACTIVE" : "ACTIVE"}
                </span>?
              </p>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-slate-800 flex items-center justify-end gap-3 bg-gray-50/50 dark:bg-slate-900/40">
              <button 
                type="button" 
                onClick={() => setUserToToggleStatus(null)}
                className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-slate-300 border border-gray-300 dark:border-slate-700 hover:bg-gray-100 dark:hover:bg-slate-800"
              >
                Cancel
              </button>
              <button 
                type="submit" 
                disabled={isTogglingStatus}
                className={`${
                  userToToggleStatus.status === "active" 
                    ? "bg-amber-600 hover:bg-amber-700" 
                    : "bg-emerald-600 hover:bg-emerald-700"
                } text-white px-4 py-2 text-xs font-bold uppercase tracking-wider disabled:opacity-40 transition-all`}
              >
                {isTogglingStatus 
                  ? "Updating…" 
                  : userToToggleStatus.status === "active" ? "Deactivate" : "Reactivate"
                }
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Netlify-Style Delete User Confirmation Modal ── */}
      {userToDelete && (
        <div className="fixed inset-0 bg-black/10 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <form
            onSubmit={confirmDeleteUser}
            className="w-full max-w-md bg-white dark:bg-[#0F172A] border dark:border-slate-800 flex flex-col shadow-2xl"
            data-testid="delete-user-modal"
          >
            <div className="p-6 border-b border-gray-200 dark:border-slate-800 flex items-start justify-between">
              <div className="flex items-center gap-2 text-rose-600 dark:text-rose-500">
                <Warning size={20} weight="bold" />
                <h2 className="text-lg font-bold tracking-tight text-slate-900 dark:text-white">Delete user</h2>
              </div>
              <button 
                type="button" 
                onClick={() => setUserToDelete(null)} 
                className="shrink-0 text-slate-400"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                This action <strong className="text-slate-900 dark:text-white font-semibold">cannot</strong> be undone. This will permanently delete <strong className="text-slate-900 dark:text-white font-semibold">{userToDelete.name}</strong>.
              </p>

              <div>
                <label className="text-xs text-slate-500 dark:text-slate-400 block mb-2">
                  Please type <strong className="text-slate-900 dark:text-slate-100 font-mono select-all">{userToDelete.email}</strong> to confirm:
                </label>
                <input
                  type="text"
                  autoFocus
                  value={deleteConfirmationText}
                  onChange={(e) => setDeleteConfirmationText(e.target.value)}
                  placeholder={userToDelete.email}
                  className="w-full border border-gray-300 dark:border-slate-700 bg-white dark:bg-[#131C31] text-slate-900 dark:text-white px-3 py-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-rose-500"
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-slate-800 flex items-center justify-end gap-3 bg-gray-50/50 dark:bg-slate-900/40">
              <button 
                type="button" 
                onClick={() => setUserToDelete(null)}
                className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-slate-300 border border-gray-300 dark:border-slate-700 hover:bg-gray-100 dark:hover:bg-slate-800"
              >
                Cancel
              </button>
              <button 
                type="submit" 
                disabled={deleteConfirmationText !== userToDelete.email || isDeleting}
                className="bg-rose-600 hover:bg-rose-700 text-white px-4 py-2 text-xs font-bold uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                {isDeleting ? "Deleting…" : "Delete User"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Create User Modal ── */}
      {drawerOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <form
            onSubmit={createUser}
            className="w-full max-w-md bg-white dark:bg-[#0F172A] border dark:border-slate-800 max-h-[90vh] overflow-y-auto flex flex-col shadow-2xl"
            data-testid="create-user-form"
          >
            <div className="p-6 border-b border-gray-200 dark:border-slate-800 flex items-start justify-between">
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0014D8] dark:text-blue-400 flex items-center gap-1.5">
                  <User size={12} weight="bold" /> Invite User
                </div>
                <h2 className="text-2xl font-black tracking-tight mt-1 text-slate-900 dark:text-white">Add new user</h2>
              </div>
              <button 
                type="button" 
                onClick={() => setDrawerOpen(false)} 
                className="shrink-0 ml-4 text-slate-400"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-6 space-y-5 flex-1">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-gray-600 dark:text-slate-300 block mb-1.5">
                  <span className="inline-flex items-center gap-1.5"><User size={12} weight="bold" /> Full Name</span>
                </label>
                <input 
                  required 
                  value={form.name} 
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="new-user-name" 
                  placeholder="e.g. Sarah Johnson"
                  className="w-full border border-gray-300 dark:border-slate-700 bg-white dark:bg-[#131C31] text-slate-900 dark:text-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" 
                />
              </div>

              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-gray-600 dark:text-slate-300 block mb-1.5">
                  <span className="inline-flex items-center gap-1.5"><EnvelopeSimple size={12} weight="bold" /> Work Email</span>
                </label>
                <input 
                  required 
                  type="email" 
                  value={form.email} 
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="new-user-email" 
                  placeholder="e.g. sarah@company.com"
                  className="w-full border border-gray-300 dark:border-slate-700 bg-white dark:bg-[#131C31] text-slate-900 dark:text-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" 
                />
              </div>

              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-gray-600 dark:text-slate-300 block mb-1.5">
                  Temporary Password
                </label>
                <input 
                  required 
                  type="text" 
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  data-testid="new-user-password" 
                  placeholder="Min. 8 characters"
                  className="w-full border border-gray-300 dark:border-slate-700 bg-white dark:bg-[#131C31] text-slate-900 dark:text-white px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-600" 
                />
              </div>

              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-gray-600 dark:text-slate-300 block mb-1.5">
                  <span className="inline-flex items-center gap-1.5"><Buildings size={12} weight="bold" /> Department</span>
                </label>
                <select 
                  value={form.department} 
                  onChange={(e) => setForm({ ...form, department: e.target.value })}
                  data-testid="new-user-department"
                  className="w-full border border-gray-300 dark:border-slate-700 bg-white dark:bg-[#131C31] text-slate-900 dark:text-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
                >
                  <option value="">— Select department —</option>
                  {DEPARTMENTS.map((d) => <option key={d}>{d}</option>)}
                </select>
              </div>

              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-gray-600 dark:text-slate-300 block mb-1.5">
                  <span className="inline-flex items-center gap-1.5"><IdentificationCard size={12} weight="bold" /> Role</span>
                </label>
                <select 
                  value={form.role} 
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  data-testid="new-user-role"
                  className="w-full border border-gray-300 dark:border-slate-700 bg-white dark:bg-[#131C31] text-slate-900 dark:text-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
                >
                  {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>

              {formErr && (
                <div className="text-xs text-rose-500 border border-rose-500/30 bg-rose-500/10 px-3 py-2" data-testid="create-user-error">
                  {formErr}
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-slate-800 flex items-center justify-end gap-3">
              <button 
                type="button" 
                onClick={() => setDrawerOpen(false)}
                className="px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-slate-300 border border-gray-300 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-800"
              >
                Cancel
              </button>
              <button 
                type="submit" 
                disabled={formBusy} 
                data-testid="create-user-submit"
                className="bg-[#0020C2] hover:bg-[#0018a3] text-white px-5 py-2.5 text-xs font-bold uppercase tracking-wider disabled:opacity-50"
              >
                {formBusy ? "Creating…" : "Create user"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}